/**
 * AddReviewersModal — author-side modal to manage reviewers on an active review.
 *
 * Sits on top of the review (accessed via ReviewStatusPanel). Lets authors:
 *   - See current reviewers with status badges
 *   - Remove reviewers that haven't already completed (status !== 'REVIEWED')
 *   - Add new reviewers in batches
 *
 * DsProvider contract: services.review (session-authed, addReviewer/removeReviewer),
 * translate, notify. Queries are invalidated via TanStack Query using the ds-*
 * namespaced keys used by other shared review components.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDsServices, useDsTranslate, useDsNotify } from '../context/DsProvider';
import { Button } from './ui/Button';
import type {
  DocumentReview,
  DocumentReviewer,
  ReviewTargetType,
  ReviewerStatus,
} from '../types/review';
import styles from './AddReviewersModal.module.scss';

interface AddReviewersModalProps {
  review: DocumentReview;
  targetType: ReviewTargetType;
  targetId: string;
  onClose: () => void;
}

interface ReviewerRow {
  name: string;
  email: string;
}

const STATUS_CLASS: Record<ReviewerStatus, string> = {
  PENDING: styles.statusPending!,
  SENT: styles.statusSent!,
  VIEWED: styles.statusViewed!,
  REVIEWED: styles.statusReviewed!,
};

export function AddReviewersModal({
  review,
  targetType,
  targetId,
  onClose,
}: AddReviewersModalProps) {
  const { review: reviewService } = useDsServices();
  const t = useDsTranslate();
  const notify = useDsNotify();
  const queryClient = useQueryClient();
  const [newRows, setNewRows] = useState<ReviewerRow[]>([{ name: '', email: '' }]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
    if (targetType === 'TEMPLATE') {
      queryClient.invalidateQueries({ queryKey: ['ds-template', targetId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['dsEnvelope', targetId] });
    }
  }, [queryClient, targetType, targetId]);

  const applyReviewToCache = useCallback(
    (updated: DocumentReview) => {
      queryClient.setQueryData<DocumentReview[]>(['ds-reviews', targetType, targetId], (prev) => {
        if (!prev) return [updated];
        return prev.map((r) => (r.id === updated.id ? updated : r));
      });
    },
    [queryClient, targetType, targetId],
  );

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!reviewService) throw new Error('Review service not configured');
      const valid = newRows.filter((r) => r.name.trim() && r.email.trim());
      let last: DocumentReview | null = null;
      for (const r of valid) {
        last = await reviewService.addReviewer(review.id, r.name.trim(), r.email.trim());
      }
      return last;
    },
    onSuccess: (updated) => {
      if (updated) applyReviewToCache(updated);
      invalidateAll();
      setNewRows([{ name: '', email: '' }]);
      notify({
        variant: 'success',
        title: t('K_DS_REVIEWERS_ADDED', 'Reviewers added'),
      });
    },
    onError: (err: Error) => {
      notify({
        variant: 'critical',
        title: err.message || t('K_DS_REVIEWERS_ADD_FAILED', 'Failed to add reviewers'),
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (reviewerId: string) => {
      if (!reviewService) throw new Error('Review service not configured');
      return reviewService.removeReviewer(review.id, reviewerId);
    },
    onSuccess: invalidateAll,
    onError: (err: Error) => {
      notify({
        variant: 'critical',
        title: err.message || t('K_DS_REVIEWER_REMOVE_FAILED', 'Failed to remove reviewer'),
      });
    },
  });

  const addRow = useCallback(() => {
    setNewRows((prev) => [...prev, { name: '', email: '' }]);
  }, []);

  const removeRow = useCallback((index: number) => {
    setNewRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateRow = useCallback((index: number, field: 'name' | 'email', value: string) => {
    setNewRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }, []);

  const existingEmails = new Set(review.reviewers.map((r) => r.email.toLowerCase()));
  const validNewRows = newRows.filter((r) => r.name.trim() && r.email.trim());
  const hasDuplicate = validNewRows.some((r) =>
    existingEmails.has(r.email.trim().toLowerCase()),
  );
  const canSubmit = validNewRows.length > 0 && !hasDuplicate;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{t('K_DS_MANAGE_REVIEWERS', 'Manage Reviewers')}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <span className={styles.sectionLabel}>
              {t('K_DS_CURRENT_REVIEWERS', 'Current Reviewers')}
            </span>
            <div className={styles.reviewerList}>
              {review.reviewers.length === 0 && (
                <div className={styles.emptyNote}>
                  {t('K_DS_NO_REVIEWERS_YET', 'No reviewers yet.')}
                </div>
              )}
              {review.reviewers.map((r: DocumentReviewer) => {
                const canRemove = r.status !== 'REVIEWED';
                const isRemoving =
                  removeMutation.isPending && removeMutation.variables === r.id;
                return (
                  <div key={r.id} className={styles.reviewerItem}>
                    <div className={styles.reviewerInfo}>
                      <span className={styles.reviewerName}>{r.name}</span>
                      <span className={styles.reviewerEmail}>{r.email}</span>
                    </div>
                    <div className={styles.reviewerItemRight}>
                      <span
                        className={`${styles.reviewerStatusBadge} ${STATUS_CLASS[r.status]}`}
                      >
                        {r.status.toLowerCase()}
                      </span>
                      <button
                        className={styles.reviewerRemoveBtn}
                        onClick={() => {
                          // eslint-disable-next-line no-alert
                          if (
                            window.confirm(
                              t(
                                'K_DS_CONFIRM_REMOVE_REVIEWER',
                                'Remove this reviewer? Their access link will be invalidated.',
                              ),
                            )
                          ) {
                            removeMutation.mutate(r.id);
                          }
                        }}
                        disabled={!canRemove || isRemoving}
                        title={
                          canRemove
                            ? t('K_DS_REMOVE_REVIEWER', 'Remove reviewer')
                            : t(
                                'K_DS_CANNOT_REMOVE_COMPLETED',
                                'Cannot remove a reviewer who has already reviewed',
                              )
                        }
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.sectionLabel}>
              {t('K_DS_ADD_REVIEWERS', 'Add Reviewers')}
            </span>
            {newRows.map((row, index) => (
              <div key={index} className={styles.reviewerRow}>
                <input
                  className={styles.input}
                  placeholder={t('K_DS_PLACEHOLDER_NAME', 'Name')}
                  value={row.name}
                  onChange={(e) => updateRow(index, 'name', e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder={t('K_DS_PLACEHOLDER_EMAIL', 'Email')}
                  type="email"
                  value={row.email}
                  onChange={(e) => updateRow(index, 'email', e.target.value)}
                />
                {newRows.length > 1 && (
                  <button
                    className={styles.rowRemoveBtn}
                    onClick={() => removeRow(index)}
                    aria-label={t('K_DS_REMOVE_ROW', 'Remove row')}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button className={styles.addRowBtn} onClick={addRow}>
              + {t('K_DS_ADD_ANOTHER', 'Add Another')}
            </button>
          </div>

          {hasDuplicate && (
            <div className={styles.errorMsg}>
              {t('K_DS_DUPLICATE_REVIEWER', 'One or more emails match an existing reviewer.')}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('K_DS_CLOSE', 'Close')}
          </Button>
          <Button
            size="sm"
            loading={addMutation.isPending}
            disabled={!canSubmit}
            onClick={() => addMutation.mutate()}
          >
            {t('K_DS_ADD_N_REVIEWERS', 'Add')}
            {validNewRows.length > 0 ? ` ${validNewRows.length} ` : ' '}
            {validNewRows.length === 1
              ? t('K_DS_REVIEWER_SINGULAR', 'Reviewer')
              : t('K_DS_REVIEWER_PLURAL', 'Reviewers')}
          </Button>
        </div>
      </div>
    </div>
  );
}
